"use client";
import React, { useState } from "react";
import { useSearchParams } from "next/navigation";
import AppliedFilters from "../Filters/AppliedFilters";
import { BalcaoBUPI } from "./CardBalcoes";
import PaginationControls from "../Pagination/PaginationControls";
import { useTranslations } from "next-intl";
import {
  DropdownOption,
  DropdownSection,
  InputSelect,
} from "@ama-pt/agora-design-system";

interface ServiceCountListProps {
  serviceCounterData?: any;
  districts?: any[];
  municipalities?: any[];
}

export default function ServiceCountList({
  serviceCounterData: initialData,
  districts = [],
  municipalities = [],
}: ServiceCountListProps) {
  const ITEMS_PER_PAGE = 12;
  const searchParams = useSearchParams();
  const t = useTranslations("Filters");

  // Estados
  const [serviceCounterData, setServiceCounterData] = useState(initialData);
  const [selectedDistrict, setSelectedDistrict] = useState<string>("");
  const [selectedMunicipality, setSelectedMunicipality] = useState<string>("");
  const [municipios, setMunicipios] = useState(municipalities);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);
  const [loading, setLoading] = useState(false);

  const distritos = districts.map((district) => ({
    value: district.designacao.toLowerCase().replace(/\s+/g, "_"),
    label: district.designacao,
    id: district.id,
  }));

  // Função de busca
  const fetchData = async (districtValue?: string, municipalityValue?: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (districtValue) {
        const district = distritos.find(d => d.value === districtValue);
        if (district) {
          params.append('distrito', district.id.toString());
        }
      }
      
      if (municipalityValue && districtValue) {
        const district = distritos.find(d => d.value === districtValue);
        if (district) {
          const municipalitiesResponse = await fetch(`/api/municipalities/${district.id}`);
          const municipalitiesData = await municipalitiesResponse.json();
          const municipality = municipalitiesData.find((m: any) => 
            m.designacao.toLowerCase().replace(/\s+/g, "_") === municipalityValue
          );
          if (municipality) {
            params.append('concelho', municipality.id.toString());
          }
        }
      }

      const response = await fetch(`/api/service-counters?${params.toString()}`);
      const data = await response.json();
      setServiceCounterData(data.serviceCounterData);
    } catch (error) {
      console.error("Erro:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMunicipalities = async (districtId: number) => {
    setLoadingMunicipalities(true);
    try {
      const response = await fetch(`/api/municipalities/${districtId}`);
      const municipalities = await response.json();
      setMunicipios(municipalities);
    } catch (error) {
      console.error("Erro ao carregar concelhos:", error);
    } finally {
      setLoadingMunicipalities(false);
    }
  };

  // Handlers
  const handleDistrictChange = (selected: any[]) => {
    const selectedOption = selected.length > 0 ? selected[0] : null;
    const districtValue = selectedOption ? selectedOption.value : "";
    
    setSelectedDistrict(districtValue);
    setSelectedMunicipality("");
    
    if (districtValue) {
      const district = distritos.find(d => d.value === districtValue);
      if (district) {
        loadMunicipalities(district.id);
      }
      fetchData(districtValue, "");
    } else {
      setServiceCounterData(initialData);
      setMunicipios([]);
    }
  };

  const handleMunicipalityChange = (selected: any[]) => {
    const selectedOption = selected.length > 0 ? selected[0] : null;
    const municipalityValue = selectedOption ? selectedOption.value : "";
    
    setSelectedMunicipality(municipalityValue);
    fetchData(selectedDistrict, municipalityValue);
  };

  // Callbacks para AppliedFilters
  const handleRemoveFilter = (filterType: string) => {
    if (filterType === 'district') {
      setSelectedDistrict("");
      setSelectedMunicipality("");
      setMunicipios([]);
      setServiceCounterData(initialData);
    } else if (filterType === 'municipality') {
      setSelectedMunicipality("");
      fetchData(selectedDistrict, "");
    }
  };

  const handleClearAllFilters = () => {
    setSelectedDistrict("");
    setSelectedMunicipality("");
    setMunicipios([]);
    setServiceCounterData(initialData);
  };

  // Cálculos
  const getPageFromUrl = () => {
    const page = searchParams?.get("page");
    return page ? parseInt(page) : 0;
  };

  const totalItems = serviceCounterData?.docs?.length || 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const currentPage = getPageFromUrl();
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedData = serviceCounterData?.docs?.slice(startIndex, endIndex) || [];

  return (
    <div>
      <div className="flex flex-col xl:flex-row gap-[64px] xl:gap-[136px]">
        <div className="w-full xl:w-3/12">
          <div className="space-y-24">
            <InputSelect
              label={t("Districts")}
              placeholder={t("selectOption")}
              hideSectionNames={true}
              dropdownAriaLabel={t("listDistricts")}
              type="text"
              onChange={handleDistrictChange}
            >
              <DropdownSection
                className={distritos.length > 4 ? "h-[300px]" : `h-[${distritos.length * 62}px]`}
                label={t("Districts")}
                name="districts"
              >
                {distritos.map((item) => (
                  <DropdownOption
                    key={item.value}
                    value={item.value}
                    selected={selectedDistrict === item.value}
                  >
                    {item.label}
                  </DropdownOption>
                ))}
              </DropdownSection>
            </InputSelect>

            <InputSelect
              label={t("Councils")}
              placeholder={
                !selectedDistrict
                  ? t("selectDistrict")
                  : loadingMunicipalities
                  ? t("loadingCouncil")
                  : t("selectOption")
              }
              hideSectionNames={true}
              dropdownAriaLabel={t("listCouncils")}
              type="text"
              onChange={handleMunicipalityChange}
              disabled={!selectedDistrict || loadingMunicipalities}
            >
              <DropdownSection
                className={municipios.length > 4 ? "h-[300px]" : `h-[${municipios.length * 62}px]`}
                label={t("Councils")}
                name="councils"
              >
                {municipios.map((item) => (
                  <DropdownOption
                    key={item.id}
                    value={item.designacao.toLowerCase().replace(/\s+/g, "_")}
                    selected={selectedMunicipality === item.designacao.toLowerCase().replace(/\s+/g, "_")}
                  >
                    {item.designacao}
                  </DropdownOption>
                ))}
              </DropdownSection>
            </InputSelect>

            {loadingMunicipalities && (
              <div className="flex items-center justify-center py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-sm text-gray-600">{t("loadingCouncil")}</span>
              </div>
            )}
          </div>

          <AppliedFilters
            district={selectedDistrict || undefined}
            districtLabel={selectedDistrict?.replace(/_/g, " ") || undefined}
            municipalityLabel={selectedMunicipality?.replace(/_/g, " ") || undefined}
            municipality={selectedMunicipality || undefined}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
          />
        </div>
        
        <div className="w-full xl:w-9/12">
          <div className="flex flex-col gap-[32px]">
            <span className="text-l-regular text-neutral-900">
              {loading ? t("loading") : `${totalItems} ${t("result")}${totalItems !== 1 ? "s" : ""}`}
            </span>
            <div className="border-t border-neutral-200" role="separator" aria-hidden="true"></div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4">{t("searchResults")}</p>
              </div>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((item: any, index: number) => (
                <BalcaoBUPI
                  key={item.id || index}
                  address={item?.address}
                  name={item?.name}
                  contact={item?.contact}
                  district={item?.district}
                  municipality={item?.municipality}
                  email={item?.email}
                />
              ))
            ) : (
              <div className="text-center py-8">
                <p>
                  {serviceCounterData === null
                    ? t("smsError")
                    : "Nenhum resultado encontrado"}
                </p>
              </div>
            )}
          </div>

          {totalItems > 0 && !loading && (
            <div className="mt-32 flex justify-center">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
